// Library Methods
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState } from "react";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "src/components/shared/alerts/Alert/Alert";
import VideoCard from "src/pages/KnowledgeHub/TutorialVideos/VideoCard/VideoCard";
import FullVideo from "./FullVideo/FullVideo";
import { CancelIcon, SearchIcon } from "src/assets/icons";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Config
import { priorityLanguageMap } from "src/config/languages";

const TutorialVideos = () => {
  const { t } = useTranslation();

  const theme = useTheme();
  const { onSurface, inputBorder } = getSurfaceTokens(theme);
  const smallerThanMd = useMediaQuery(theme.breakpoints.down("md"));

  const [searchTerm, setSearchTerm] = useState("");

  const onClear = useCallback(() => {
    setSearchTerm("");
  }, []);

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setSearchTerm(e?.target?.value);
    },
    []
  );

  // TODO: Update actual text under each video
  const placeholderText = "";
  // "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Pellentesque nulla nulla, porta at arcu nec, sodales aliquet libero. Donec sollicitudin est ante, sit amet mattis odio efficitur sollicitudin. Sed porta purus vel dolor tristique vehicula. Sed tincidunt ex id augue aliquet, id mattis ante finibus. Nam at est vel lorem euismod lacinia non ut libero. In facilisis quis nibh id rhoncus.";

  const tutorialVideos = useMemo(
    () => [
      {
        id: "1jgptVD3_KA",
        index: 0,
        name: t("Login to the Portal"),
        length: "1:19",
        frLength: "1:17",
        thumbnail: "https://img.youtube.com/vi/1jgptVD3_KA/hqdefault.jpg",
        url: "https://www.youtube.com/embed/1jgptVD3_KA?si=S4Q2Fx6G1kQ53hUL",
        frThumbnail: "https://img.youtube.com/vi/OCwwofgpF_k/hqdefault.jpg",
        frUrl: "https://www.youtube.com/embed/OCwwofgpF_k?si=3UVMvXY-X6pPMIiX",
        description: placeholderText,
        timestamps: [
          {
            time: "0:10",
            seconds: 10,
            frTime: "0:12",
            frSeconds: 12,
            label: t("Set up your password"),
          },
          {
            time: "1:02",
            seconds: 62,
            frTime: "1:02",
            frSeconds: 62,
            label: t("Security tips"),
          },
        ],
      },
      {
        id: "47zkLGzcWEQ",
        index: 1,
        name: t("Learn About Your Headset"),
        length: "3:50",
        frLength: "3:00",
        thumbnail: "https://img.youtube.com/vi/47zkLGzcWEQ/hqdefault.jpg",
        url: "https://www.youtube.com/embed/47zkLGzcWEQ?si=Qnqyyup6gjEdbJoI",
        frThumbnail: "https://img.youtube.com/vi/FZ1KQyGFf88/hqdefault.jpg",
        frUrl: "https://www.youtube.com/embed/FZ1KQyGFf88?si=2JM0DihCRTA1AJUC",
        description: placeholderText,
        timestamps: [
          {
            time: "0:10",
            seconds: 10,
            frTime: "0:09",
            frSeconds: 9,
            label: t("How to turn on the headset"),
          },
          {
            time: "0:55",
            seconds: 55,
            frTime: "0:52",
            frSeconds: 52,
            label: t("How to put the headset on"),
          },
          {
            time: "1:24",
            seconds: 84,
            frTime: "1:11",
            frSeconds: 71,
            label: t("How to use the controller"),
          },
          {
            time: "2:38",
            seconds: 158,
            frTime: "2:05",
            frSeconds: 125,
            label: t("How to care for the headset and controller"),
          },
          {
            time: "2:56",
            seconds: 176,
            frTime: "2:23",
            frSeconds: 143,
            label: t("Safety tips"),
          },
        ],
      },
      {
        id: "sgFR5WBeBP4",
        index: 2,
        name: t("Connecting Your Headset to Wi-Fi"),
        length: "1:39",
        frLength: "1:45",
        thumbnail: "https://img.youtube.com/vi/sgFR5WBeBP4/maxresdefault.jpg",
        url: "https://www.youtube.com/embed/sgFR5WBeBP4?si=0GtY7m7iL4JwZocP",
        frThumbnail: "https://img.youtube.com/vi/MVlqn7Sl76s/hqdefault.jpg",
        frUrl: "https://www.youtube.com/embed/MVlqn7Sl76s?si=9Bjs436j8BcoqKu3",
        description: placeholderText,
        timestamps: [
          {
            time: "0:05",
            seconds: 5,
            frTime: "0:03",
            frSeconds: 3,
            label: t("Requirements for connecting to Wi-Fi"),
          },
          {
            time: "0:18",
            seconds: 18,
            frTime: "0:12",
            frSeconds: 12,
            label: t("Default starting screen"),
          },
          {
            time: "0:36",
            seconds: 36,
            frTime: "0:21",
            frSeconds: 21,
            label: t("Quick access menu"),
          },
          {
            time: "1:04",
            seconds: 64,
            frTime: "1:12",
            frSeconds: 72,
            label: t("Return to the lobby"),
          },
        ],
      },
      {
        id: "BEsZ04-fins",
        index: 3,
        name: t("Add and Link Exams"),
        length: "3:17",
        frLength: "3:20",
        thumbnail: "https://img.youtube.com/vi/BEsZ04-fins/maxresdefault.jpg",
        url: "https://www.youtube.com/embed/BEsZ04-fins?si=owaErVLWyo5p_4mr",
        frThumbnail: "https://img.youtube.com/vi/8pGBMaA7-_U/maxresdefault.jpg",
        frUrl: "https://www.youtube.com/embed/8pGBMaA7-_U?si=_fawG5sAl6XlFDQB",
        description: placeholderText,
        timestamps: [
          {
            time: "0:08",
            seconds: 8,
            frTime: "0:09",
            frSeconds: 9,
            label: t("Navbar tabs"),
          },
          {
            time: "0:23",
            seconds: 23,
            frTime: "0:23",
            frSeconds: 23,
            label: t("Patients page overview"),
          },
          {
            time: "0:37",
            seconds: 37,
            frTime: "0:37",
            frSeconds: 37,
            label: t("Add a new patient"),
          },
          {
            time: "1:09",
            seconds: 69,
            frTime: "1:10",
            frSeconds: 70,
            label: t("Add a new exam"),
          },
          {
            time: "2:03",
            seconds: 123,
            frTime: "2:04",
            frSeconds: 124,
            label: t("Link a device"),
          },
          {
            time: "2:33",
            seconds: 153,
            frTime: "2:33",
            frSeconds: 153,
            label: t("Unlink a device"),
          },
          {
            time: "2:40",
            seconds: 160,
            frTime: "2:43",
            frSeconds: 163,
            label: t("Tips and tricks"),
          },
        ],
      },
      {
        id: "gIUh1UEn6zw",
        index: 4,
        name: t("Create Preset Exam Packages"),
        length: "1:22",
        frLength: "1:26",
        thumbnail: "https://img.youtube.com/vi/gIUh1UEn6zw/sddefault.jpg",
        url: "https://www.youtube.com/embed/gIUh1UEn6zw?si=3m65VinU1J4pqVnS",
        frThumbnail: "https://img.youtube.com/vi/Uc0yHYX_D-8/maxresdefault.jpg",
        frUrl: "https://www.youtube.com/embed/Uc0yHYX_D-8?si=6MBCodGlHIsdQ8KT",
        description: placeholderText,
        timestamps: [
          {
            time: "0:10",
            seconds: 10,
            frTime: "0:07",
            frSeconds: 7,
            label: t("Use cases for exam packages"),
          },
          {
            time: "0:29",
            seconds: 29,
            frTime: "0:29",
            frSeconds: 29,
            label: t("Set up an exam package"),
          },
          {
            time: "0:58",
            seconds: 58,
            frTime: "1:03",
            frSeconds: 63,
            label: t("Quick add buttons"),
          },
        ],
      },
      {
        id: "7xhQ8KcBP4g",
        index: 5,
        name: t("Conducting an Exam"),
        length: "4:47",
        frLength: "4:14",
        thumbnail: "https://img.youtube.com/vi/7xhQ8KcBP4g/maxresdefault.jpg",
        url: "https://www.youtube.com/embed/7xhQ8KcBP4g?si=WgzBYhlb9w3nRaU1",
        frThumbnail: "https://img.youtube.com/vi/uKFCe86BiwA/maxresdefault.jpg",
        frUrl: "https://www.youtube.com/embed/uKFCe86BiwA?si=lIWuwKIXfPCvMj7K",
        description: placeholderText,
        timestamps: [
          {
            time: "0:19",
            seconds: 19,
            frTime: "0:19",
            frSeconds: 19,
            label: t("Open the live update panel"),
          },
          {
            time: "0:29",
            seconds: 29,
            frTime: "0:31",
            frSeconds: 31,
            label: t("How to guide the patient"),
          },
          {
            time: "0:44",
            seconds: 44,
            frTime: "0:45",
            frSeconds: 45,
            label: t("How to wear the headset"),
          },
          {
            time: "1:24",
            seconds: 84,
            frTime: "1:23",
            frSeconds: 83,
            label: t("Live update panel overview"),
          },
          {
            time: "1:49",
            seconds: 109,
            frTime: "1:44",
            frSeconds: 104,
            label: t("Start the exam"),
          },
          {
            time: "2:01",
            seconds: 121,
            frTime: "1:55",
            frSeconds: 115,
            label: t("Visual field exam overview"),
          },
          {
            time: "2:29",
            seconds: 149,
            frTime: "1:19",
            frSeconds: 139,
            label: t("Live update options"),
          },
          {
            time: "2:54",
            seconds: 174,
            frTime: "2:37",
            frSeconds: 157,
            label: t("Multi-exam display"),
          },
          {
            time: "3:22",
            seconds: 202,
            frTime: "2:58",
            frSeconds: 178,
            label: t("How to remove the headset"),
          },
          {
            time: "3:36",
            seconds: 216,
            frTime: "3:12",
            frSeconds: 192,
            label: t("See completed results"),
          },
          {
            time: "3:45",
            seconds: 225,
            frTime: "3:21",
            frSeconds: 201,
            label: t("Tips and tricks"),
          },
        ],
      },
      {
        id: "_pbbjIHgH5I",
        index: 6,
        name: t("How to Pair Your Controller"),
        length: "2:58",
        frLength: "",
        thumbnail: "https://img.youtube.com/vi/_pbbjIHgH5I/maxresdefault.jpg",
        url: "https://www.youtube.com/embed/_pbbjIHgH5I?si=WgzBYhlb9w3nRaU1",
        frThumbnail: "",
        frUrl: "",
        description: placeholderText,
        timestamps: [
          {
            time: "0:05",
            seconds: 5,
            frTime: "",
            frSeconds: 0,
            label: t("How to know if the controller is disconnected"),
          },
          {
            time: "0:09",
            seconds: 9,
            frTime: "",
            frSeconds: 0,
            label: t("Obtain a code to re-pair the controller"),
          },
          {
            time: "0:24",
            seconds: 24,
            frTime: "",
            frSeconds: 0,
            label: t("Navigate to the headset's Arbor menu"),
          },
          {
            time: "0:44",
            seconds: 44,
            frTime: "",
            frSeconds: 0,
            label: t("Enter code into the Arbor menu"),
          },
          {
            time: "0:56",
            seconds: 56,
            frTime: "",
            frSeconds: 0,
            label: t("Pair the controller from the standard menu"),
          },
          {
            time: "1:40",
            seconds: 100,
            frTime: "",
            frSeconds: 0,
            label: t("Alternative steps for pairing the controller"),
          },
          {
            time: "2:35",
            seconds: 155,
            frTime: "",
            frSeconds: 0,
            label: t("Return to the Retinalogik app"),
          },
        ],
      },
    ],
    [t]
  );

  const [startTime, setStartTime] = useState(0);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(tutorialVideos[0]);

  const filteredTutorials = useMemo(() => {
    const currentLang = i18next.language as keyof typeof priorityLanguageMap;
    const filteredByLanguage = tutorialVideos.filter(
      (video) =>
        (currentLang === "en" && video.url) ||
        (currentLang === "fr" && video.frUrl)
    );

    if (!searchTerm.trim()) return filteredByLanguage;
    const query = searchTerm.toLowerCase();

    return filteredByLanguage.filter(
      (video) =>
        video.name.toLowerCase().includes(query) ||
        video.description.toLowerCase().includes(query)
    );
  }, [searchTerm, tutorialVideos]);

  const handleSelectVideo = useCallback((video: any) => {
    setSelectedVideo(video);
    setStartTime(0);
    setShouldAutoplay(false);
  }, []);

  const handleTimestampClick = useCallback((seconds: number) => {
    setStartTime(seconds);
    setShouldAutoplay(true);
  }, []);

  return (
    <Grid
      sx={{ display: "flex", flexDirection: smallerThanMd ? "column" : "row" }}
    >
      <Grid
        item
        sx={{
          display: "flex",
          flexDirection: "column",
          alignSelf: "flex-start",
          justifyContent: "center",
          gap: "8px",
          minWidth: smallerThanMd ? "100%" : "350px",
          maxWidth: smallerThanMd ? "100%" : "350px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>
            {t("Tutorial Videos")}
          </Typography>
          <Typography sx={{ fontSize: "14px" }}>
            {t("{{ count }} video", {
              count: filteredTutorials?.length,
            })}
          </Typography>
        </Box>

        <TextField
          autoFocus
          id="tutorial-videos-input"
          placeholder={t("Search...")}
          value={searchTerm}
          variant="standard"
          inputProps={{
            sx: {
              "&::placeholder": {
                color: inputBorder,
                opacity: 1,
              },
            },
          }}
          InputProps={{
            disableUnderline: true,
            style: {
              backgroundColor: "#5B6D7A10",
              color: onSurface,
              height: "40px",
              border: "1px solid #5B6D7A",
              borderRadius: "4px",
            },
            startAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", mx: "8px" }}>
                <SearchIcon />
              </Box>
            ),
            endAdornment: (
              <Box
                data-testid="reset-searchbar-button"
                sx={{
                  display: searchTerm?.length > 0 ? "flex" : "none",
                  alignItems: "center",
                  mx: "8px",
                  cursor: "pointer",
                }}
                onClick={onClear}
              >
                <CancelIcon />
              </Box>
            ),
          }}
          data-testid="searchbar"
          onChange={onChange}
          sx={{
            width: "100%",
            mb: 0,
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: smallerThanMd ? "row" : "column",
            justifyContent: "flex-start",
            overflowY: "hidden",
            overflowX: "auto",
            flexWrap: "nowrap",
            marginTop: "8px",
            marginBottom: smallerThanMd ? "20px" : "0px",
            gap: "12px",
            pb:
              smallerThanMd &&
              filteredTutorials &&
              filteredTutorials?.length > 0
                ? "16px"
                : "0px",
          }}
        >
          {filteredTutorials?.length === 0 ? (
            <Typography textAlign="center">{t("No videos found.")}</Typography>
          ) : (
            filteredTutorials?.map((tutorialVideo) => (
              <VideoCard
                key={tutorialVideo.id}
                tutorialVideo={tutorialVideo}
                selectedVideo={selectedVideo}
                handleSelectVideo={handleSelectVideo}
              />
            ))
          )}
        </Box>
      </Grid>
      <Grid
        item
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flex: 1,
          paddingLeft: smallerThanMd ? "0px" : "20px",
        }}
      >
        <Alert
          variant="info"
          alertContent={
            <Typography fontSize="14px">
              {t("Once you've watched every video, click")}{" "}
              <span>
                <a href="/settings" style={{ textDecoration: "underline" }}>
                  {t("here")}
                </a>
              </span>{" "}
              {t("to adjust your clinic settings.")}
            </Typography>
          }
        />
        <FullVideo
          selectedVideo={selectedVideo}
          startTime={startTime}
          shouldAutoplay={shouldAutoplay}
          handleTimestampClick={handleTimestampClick}
        />
      </Grid>
    </Grid>
  );
};

export default TutorialVideos;
